
import React, { useState, useEffect } from 'react';
import { Control, useWatch, useFormContext } from 'react-hook-form';
import { motion } from 'framer-motion';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { MALE_SIZES, FEMALE_SIZES } from './size-data';
import GenderTabs from './GenderTabs';
import SizeButtons from './SizeButtons';
import SizeInfoDisplay from './SizeInfoDisplay';
import SizeReminderNote from './SizeReminderNote';
import ShirtIcon from './ShirtIcon';
import { SizeOption } from './types';

interface TShirtSizeSelectorProps {
  control: Control<any>;
}

const TShirtSizeSelector: React.FC<TShirtSizeSelectorProps> = ({ control }) => {
  const [gender, setGender] = useState<'male' | 'female'>('male');
  const [hoveredSize, setHoveredSize] = useState<string | null>(null);
  const { setValue, getValues } = useFormContext();

  // Watch the t_shirt_size field to update gender tab when it changes
  const tShirtSize = useWatch({
    control,
    name: 't_shirt_size'
  });

  // Update the gender tab based on the t_shirt_size value
  useEffect(() => {
    if (tShirtSize) {
      if (tShirtSize.startsWith('Nam_')) {
        setGender('male');
      } else if (tShirtSize.startsWith('Nu_')) {
        setGender('female');
      }
    }
  }, [tShirtSize]);

  // Effect to save gender to the form when it changes
  useEffect(() => {
    // Store the gender in the form for database saving
    setValue('gender', gender);
    
    // Update t-shirt size with new gender prefix if needed
    const currentSize = getValues('t_shirt_size');
    if (currentSize) {
      let size = currentSize;
      
      // Strip any existing prefix
      if (size.startsWith('Nam_')) size = size.substring(4);
      else if (size.startsWith('Nu_')) size = size.substring(3);
      
      // Add the new prefix
      const newPrefix = gender === 'male' ? 'Nam_' : 'Nu_';
      const newSize = `${newPrefix}${size}`;
      
      if (newSize !== currentSize) {
        setValue('t_shirt_size', newSize);
      }
    }
  }, [gender, setValue, getValues]);

  // Get sizes based on gender
  const sizes = gender === 'male' ? MALE_SIZES : FEMALE_SIZES;
  
  // Find the current selected size from the form value
  const getCurrentSelectedSize = (): string | null => {
    const currentValue = getValues('t_shirt_size');
    if (!currentValue) return null;
    
    // Extract the size value without the gender prefix
    if (currentValue.startsWith('Nam_')) return currentValue.substring(4);
    if (currentValue.startsWith('Nu_')) return currentValue.substring(3);
    
    return currentValue;
  };
  
  // Set the hovered size to the current selected size when the component mounts
  // or when the gender or tShirtSize changes
  useEffect(() => {
    const currentSize = getCurrentSelectedSize();
    if (currentSize) {
      setHoveredSize(currentSize);
    }
  }, [gender, tShirtSize]);
  
  // Find detailed info for the hovered size
  const hoveredSizeInfo = sizes.find(size => size.value === hoveredSize);
  
  // Much brighter button colors with high contrast
  const getButtonColors = (isSelected: boolean) => {
    if (gender === 'male') {
      return isSelected
        ? 'bg-gradient-to-r from-blue-500 to-blue-400 border-blue-600 font-bold shadow-lg ring-2 ring-blue-300'
        : 'bg-blue-50 text-blue-900 border-blue-200 hover:bg-blue-100 hover:border-blue-500';
    } else {
      return isSelected
        ? 'bg-gradient-to-r from-pink-400 to-pink-300 border-pink-500 font-bold shadow-lg ring-2 ring-pink-200'
        : 'bg-pink-50 text-pink-900 border-pink-200 hover:bg-pink-100 hover:border-pink-500';
    }
  };

  // Handle gender change from tabs
  const handleGenderChange = (value: string) => {
    const newGender = value as 'male' | 'female';
    setGender(newGender);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <Tabs defaultValue="male" value={gender} onValueChange={handleGenderChange}>
        <GenderTabs gender={gender} onGenderChange={handleGenderChange} />

        <TabsContent value="male" className="mt-0 relative">
          <ShirtIcon />
          <SizeButtons 
            gender="male"
            sizes={MALE_SIZES}
            control={control}
            onHoverSize={setHoveredSize}
            getButtonColors={getButtonColors}
          />
        </TabsContent>

        <TabsContent value="female" className="mt-0 relative">
          <ShirtIcon />
          <SizeButtons 
            gender="female"
            sizes={FEMALE_SIZES}
            control={control}
            onHoverSize={setHoveredSize}
            getButtonColors={getButtonColors}
          />
        </TabsContent>
      </Tabs>

      {/* Display detailed info for hovered size */}
      <SizeInfoDisplay 
        hoveredSizeInfo={hoveredSizeInfo} 
        gender={gender} 
      />

      <SizeReminderNote gender={gender} />
    </motion.div>
  );
};

export default TShirtSizeSelector;
